/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  FormActionPipelines,
  getGtoEntryKey,
  GtoEntryPoint,
} from '@gh/builder-shared';
import { BuilderOfficeData, GtoUom, OfficeMeasurement } from '@gh/builder-shared-data';
import {
  getDialogInitData,
  getDialogInput,
  hexColorToIntColor,
  intColorToHexColor,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogInput, FormMode } from '@gh/core-ui';
import { assign, includes } from 'lodash';
import { Observable } from 'rxjs';
import {
  EstimatingTakeOffParamDetailsDialogConfig,
  EstimatingTakeOffParamsTabBusinessService,
} from '../../resources/estimating-take-off-params-tab-business.service';
import { GraphicalTakeOffTop } from '../../resources/estimating-take-off-params-tab.model';
import { EstimatingTakeOffParamFormState } from './estimating-take-off-param-details-form.service';

@Component({
  selector: 'gh-estimating-take-off-param-details-dialog',
  template: require('./estimating-take-off-param-details-dialog.component.html'),
  styles: [require('./estimating-take-off-param-details-dialog.component.scss')],
  providers: [EstimatingTakeOffParamsTabBusinessService],
  viewProviders: [EstimatingTakeOffParamFormState],
})
export class EstimatingTakeOffParamDetailsDialogComponent implements DialogInput<EstimatingTakeOffParamDetailsDialogConfig>, OnInit {
  input: EstimatingTakeOffParamDetailsDialogConfig;
  gtoUoms: TextDictionary;
  gtoStyles: TextDictionary;
  color: any;
  entryPoint: GtoEntryPoint;
  entryId: ID;

  constructor(private pipelines: FormActionPipelines,
              private businessService: EstimatingTakeOffParamsTabBusinessService,
              private commonActions: CommonActions,
              public dialogRef: MatDialogRef<EstimatingTakeOffParamDetailsDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              public form: EstimatingTakeOffParamFormState,
              private userContext: BuilderUserContext) {

  }

  get title(): string {
    if (this.entryPoint) {
      return 'lbl_top_create';
    }

    return {
      [FormMode.CreateMode]: 'lbl_top_create',
      [FormMode.EditMode]: 'lbl_top_edit',
      [FormMode.ViewMode]: 'lbl_top_view',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { takeOffParam, ...init } = getDialogInitData(this.dialogData);
    const takeOffParamId = this.input.id;

    const { entryPoint, entryId, ...input } = getDialogInput(this.dialogData);
    this.entryPoint = entryPoint;
    this.entryId = entryId;

    assign(this, init);
    this.form.build(takeOffParam);

    const measurementScale = this.userContext.office && (<BuilderOfficeData>this.userContext.office).measurementScale;

    if (measurementScale === OfficeMeasurement.Imperial) {
      this.gtoUoms = this.gtoUoms
        .filter(({ id }: any) => !includes([GtoUom.Millimeters, GtoUom.MetersSquare, GtoUom.Meters], id));
    }

    if (this.entryPoint) {
      this.gtoUoms = this.gtoUoms
        .filter(({ id }: any) => !includes(
          [GtoUom.Price, GtoUom.Other, GtoUom.NotAvailable],
          id,
        ));
    }

    if (measurementScale === OfficeMeasurement.Metric) {
      this.gtoUoms = this.gtoUoms
        .filter(({ id }: any) => !includes([GtoUom.Feets, GtoUom.FeetsSquare], id));
    }

    if (takeOffParamId) {
      if (!takeOffParam.gtoColor) {
        takeOffParam.gtoColor = 0;
      }
      this.form.setValue(takeOffParam);
      this.color = intColorToHexColor(takeOffParam.gtoColor);
    }
  }

  onSubmit(): void {
    const flow = this.pipelines.process({
      execute: this.commonActions.execute((state: CommonFlowState<any>): Observable<any> =>
        this.entryPoint
          ? this.saveForGTO(state.value)
          : this.businessService.save(state.value)),
      finish: (state: CommonFlowState<any>) => {
        this.dialogRef.close(state.value);
      },
    });

    flow.run({ form: this.form, trigger: 'process', value: this.form.submitValue });
  }

  saveForGTO(state: any): Observable<GraphicalTakeOffTop> {
    const data = assign({}, state);
    const entryIdKey = getGtoEntryKey(this.entryPoint, 'id');
    data[entryIdKey] = this.entryId;

    return this.businessService.saveForGTO(this.entryId, data, this.entryPoint);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onColorPickerChanged(hexColor: any): void {
    this.form.get('gtoColor').setValue(hexColorToIntColor(hexColor));
    this.color = hexColor;
  }
}
